import { localDate, thumbName } from './exportNaming';
import { VIEWER_CSS, VIEWER_JS } from './exportHtmlAssets';
import type { ExportMediaType } from './types';

export interface HtmlItem {
  filename: string;
  mediaType: ExportMediaType;
  hasThumb: boolean;
}

export interface AlbumHtmlInput {
  title: string;
  items: HtmlItem[];
  /** e.g. "Part 2" for one ZIP of several; null otherwise. */
  partLabel: string | null;
  exportedAt: Date;
}

export function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`;

function tile(item: HtmlItem): string {
  const label = escapeHtml(item.filename);
  const src = escapeHtml(`media/${encodeURIComponent(item.filename)}`);
  const thumb = item.hasThumb ? escapeHtml(`thumbs/${encodeURIComponent(thumbName(item.filename))}`) : '';
  // A photo with no separate preview shows itself, unless browsers cannot draw it (HEIC).
  const image = item.hasThumb ? thumb : item.mediaType === 'photo' && !/\.hei[cf]$/i.test(item.filename) ? src : '';
  const inner = image
    ? `<img loading="lazy" src="${image}" alt="${label}">`
    : `<span class="ph">${item.mediaType === 'video' ? '&#9654;' : '&#9634;'}</span>`;
  const badge = item.mediaType === 'video' ? '<span class="badge">&#9654;</span>' : '';
  return `<button type="button" class="tile" data-type="${item.mediaType}" data-src="${src}" data-thumb="${thumb}" title="${label}">${inner}${badge}</button>`;
}

/** Builds the self-contained index.html for one exported folder or ZIP part. */
export function buildAlbumHtml({ title, items, partLabel, exportedAt }: AlbumHtmlInput): string {
  const photos = items.filter(i => i.mediaType === 'photo').length;
  const videos = items.filter(i => i.mediaType === 'video').length;
  const others = items.length - photos - videos;
  const meta = [
    partLabel,
    plural(photos, 'photo'),
    videos > 0 ? plural(videos, 'video') : null,
    others > 0 ? plural(others, 'other file') : null,
    `Exported from Kizu on ${localDate(exportedAt)}`,
  ].filter(Boolean).join(' · ');
  const pageTitle = partLabel ? `${title} (${partLabel})` : title;
  const body = items.length > 0
    ? `<main class="grid">${items.map(tile).join('')}</main>`
    : '<p class="empty">This export contains no files.</p>';

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<title>${escapeHtml(pageTitle)}</title>
<style>${VIEWER_CSS}</style>
</head>
<body>
<header><h1>${escapeHtml(title)}</h1><p class="meta">${escapeHtml(meta)}</p></header>
${body}
<div id="box" hidden>
<button type="button" id="close">Close</button>
<button type="button" id="prev" class="nav">&#8249;</button>
<div id="stage"></div>
<button type="button" id="next" class="nav">&#8250;</button>
<a id="open" target="_blank" rel="noopener" hidden>Open original</a>
</div>
<script>${VIEWER_JS}</script>
</body>
</html>
`;
}
